import { useMemo, useState } from "react";
import client from "../api/client";
import * as XLSX from "xlsx";

const theme = {
  page: { fontSize: 11 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    marginBottom: 10,
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "9px 12px",
  },
  title: { fontSize: 13, fontWeight: 800, margin: 0 },
  sub: { fontSize: 10, opacity: 0.9 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 700 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    background: "#07111f",
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  stat: {
    borderRadius: 12,
    padding: 10,
    background: "#f8f9fa",
    border: "1px solid #e9ecef",
    height: "100%",
  },
};

const reportTabs = [
  {
    key: "gstr1",
    title: "GSTR-1",
    icon: "bi-file-earmark-spreadsheet",
    api: "/gst-final-reports/gstr1",
    sheet: "GSTR1",
  },
  {
    key: "gstr3b",
    title: "GSTR-3B",
    icon: "bi-file-bar-graph",
    api: "/gst-final-reports/gstr3b",
    sheet: "GSTR3B",
  },
  {
    key: "hsn",
    title: "HSN Summary",
    icon: "bi-upc-scan",
    api: "/gst-final-reports/hsn-summary",
    sheet: "HSN Summary",
  },
  {
    key: "liability",
    title: "Tax Liability",
    icon: "bi-cash-coin",
    api: "/gst-final-reports/tax-liability",
    sheet: "Tax Liability",
  },
  {
    key: "purchase",
    title: "Purchase Register",
    icon: "bi-cart-plus",
    api: "/gst-final-reports/purchase-register",
    sheet: "Purchase Register",
  },
  {
    key: "sales",
    title: "Sales Register",
    icon: "bi-receipt",
    api: "/gst-final-reports/sales-register",
    sheet: "Sales Register",
  },
];

const val = (row, name) => {
  const camel = name.charAt(0).toLowerCase() + name.slice(1);
  const value = row?.[name] ?? row?.[camel] ?? "";

  if (value === null || value === undefined) return "";
  if (typeof value === "object") return "";

  return value;
};

const numberOrZero = (v) => {
  if (v === null || v === undefined || typeof v === "object") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) => numberOrZero(v).toFixed(2);

const looksAmount = (col) =>
  /(amount|value|tax|cgst|sgst|igst|total|payable|liability|credit|cess|qty|rate)/i.test(
    col,
  );

const getAmountValue = (row, keys) => {
  for (const k of keys) {
    const v = val(row, k);
    if (v !== "") return numberOrZero(v);
  }
  return 0;
};

const getTaxableValue = (row) =>
  getAmountValue(row, [
    "TaxableValue",
    "TaxableAmount",
    "OutwardTaxableValue",
    "AssessableValue",
  ]);

const getTaxSplit = (row) => {
  const cgst = getAmountValue(row, ["CGST", "OutputCGST", "InputCGST"]);
  const sgst = getAmountValue(row, ["SGST", "OutputSGST", "InputSGST"]);
  const igst = getAmountValue(row, ["IGST", "OutputIGST", "InputIGST"]);

  if (cgst || sgst || igst) return { cgst, sgst, igst };

  const taxAmount = getAmountValue(row, [
    "TaxAmount",
    "TotalTax",
    "OutwardTax",
    "OutputTax",
    "InputTax",
  ]);

  const supplyType = String(
    val(row, "SupplyType") ||
      val(row, "Classification") ||
      val(row, "GSTClassification") ||
      "",
  ).toUpperCase();

  if (supplyType === "INTER" || supplyType === "INTERSTATE") {
    return { cgst: 0, sgst: 0, igst: taxAmount };
  }

  return { cgst: taxAmount / 2, sgst: taxAmount / 2, igst: 0 };
};

const getTotalTax = (row) => {
  const direct = getAmountValue(row, [
    "TotalTax",
    "TaxAmount",
    "OutwardTax",
    "OutputTax",
    "InputTax",
  ]);

  if (direct) return direct;

  const s = getTaxSplit(row);
  return s.cgst + s.sgst + s.igst;
};

const getGrandTotal = (row) => {
  const direct = getAmountValue(row, [
    "GrandTotal",
    "InvoiceValue",
    "InvoiceAmount",
    "BillAmount",
    "TotalAmount",
  ]);

  if (direct) return direct;

  return getTaxableValue(row) + getTotalTax(row);
};

const displayMoney = (col, value, row) => {
  const n =
    /taxable/i.test(col) && !/taxamount|tax amount/i.test(col)
      ? getTaxableValue(row)
      : /invoicevalue|invoiceamount|grandtotal|billamount|totalamount/i.test(col)
        ? getGrandTotal(row)
        : /totaltax|taxamount|outputtax|inputtax/i.test(col)
          ? getTotalTax(row)
          : numberOrZero(value);

  if (
    /nettaxpayable|nettaxliability|taxpayable|taxliability|payable/i.test(col)
  ) {
    return Math.max(n, 0).toFixed(2);
  }

  return n.toFixed(2);
};

function DataTable({ rows }) {
  const columns = useMemo(() => Object.keys(rows?.[0] || {}), [rows]);

  if (!rows?.length) {
    return (
      <div className="text-muted small p-3 text-center">
        No records found for the selected period.
      </div>
    );
  }

  return (
    <div style={{ overflowX: "auto", maxHeight: 430 }}>
      <table className="table table-sm table-bordered table-hover mb-0">
        <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
          <tr>
            {columns.map((c) => (
              <th key={c} style={theme.th}>
                {c}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => {
                const value = val(row, c);
                const isAmount = looksAmount(c);

                return (
                  <td
                    key={c}
                    style={theme.td}
                    className={isAmount ? "text-end" : ""}
                  >
                    {isAmount ? displayMoney(c, value, row) : String(value ?? "")}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function GstFinalReportsPage() {
  const [active, setActive] = useState("gstr1");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});

  const currentTab = reportTabs.find((x) => x.key === active) || reportTabs[0];
  const rows = data[active] || [];

  const totals = useMemo(() => {
    const result = {
      Taxable: 0,
      CGST: 0,
      SGST: 0,
      IGST: 0,
      TotalTax: 0,
      GrandTotal: 0,
      ITCCarryForward: 0,
    };

    rows.forEach((r) => {
      const split = getTaxSplit(r);
      const totalTax = getTotalTax(r);
      const outputTax = getAmountValue(r, ["OutwardTax", "OutputTax"]);
      const inputTax = getAmountValue(r, ["InputTaxCredit", "InputTax"]);
      const grossPayable = outputTax - inputTax;

      result.Taxable += getTaxableValue(r);
      result.CGST += split.cgst;
      result.SGST += split.sgst;
      result.IGST += split.igst;
      result.TotalTax += totalTax;
      result.GrandTotal += getGrandTotal(r);

      if (grossPayable < 0) {
        result.ITCCarryForward += Math.abs(grossPayable);
      }
    });

    return result;
  }, [rows]);

  const buildParams = () => {
    const qs = new URLSearchParams();
    qs.set("fromDate", fromDate);
    qs.set("toDate", toDate);
    if (companyId) qs.set("companyId", companyId);
    return qs.toString();
  };

  const loadOne = async (tab = currentTab) => {
    if (!fromDate || !toDate) {
      alert("Please select From Date and To Date");
      return;
    }

    try {
      setLoading(true);
      const res = await client.get(`${tab.api}?${buildParams()}`);

      setData((old) => ({
        ...old,
        [tab.key]: Array.isArray(res.data)
          ? res.data
          : res.data
            ? [res.data]
            : [],
      }));
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data ||
          `Failed to load ${tab.title}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const loadAll = async () => {
    if (!fromDate || !toDate) {
      alert("Please select From Date and To Date");
      return;
    }

    try {
      setLoading(true);

      const qs = buildParams();
      const responses = await Promise.all(
        reportTabs.map((tab) => client.get(`${tab.api}?${qs}`)),
      );

      const next = {};

      responses.forEach((res, i) => {
        next[reportTabs[i].key] = Array.isArray(res.data)
          ? res.data
          : res.data
            ? [res.data]
            : [];
      });

      setData(next);
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Failed to load GST reports",
      );
    } finally {
      setLoading(false);
    }
  };

  const cleanExportRows = (exportRows) =>
    exportRows.map((row) => {
      const next = { ...row };

      Object.keys(next).forEach((key) => {
        if (/taxable/i.test(key) && !/taxamount/i.test(key)) {
          next[key] = getTaxableValue(row);
        }

        if (/invoicevalue|invoiceamount|grandtotal|billamount|totalamount/i.test(key)) {
          next[key] = getGrandTotal(row);
        }

        if (/totaltax|taxamount|outputtax|inputtax/i.test(key)) {
          next[key] = getTotalTax(row);
        }

        if (
          looksAmount(key) &&
          /nettaxpayable|nettaxliability|taxpayable|taxliability|payable/i.test(
            key,
          )
        ) {
          next[key] = Math.max(numberOrZero(next[key]), 0);
        }
      });

      return next;
    });

  const exportActive = () => {
    if (!rows.length) {
      alert("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(cleanExportRows(rows)),
      currentTab.sheet,
    );

    XLSX.writeFile(wb, `${currentTab.sheet}_${fromDate}_to_${toDate}.xlsx`);
  };

  const exportAll = () => {
    const wb = XLSX.utils.book_new();
    let added = 0;

    reportTabs.forEach((tab) => {
      const tabRows = data[tab.key] || [];

      if (tabRows.length) {
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(cleanExportRows(tabRows)),
          tab.sheet.substring(0, 31),
        );

        added++;
      }
    });

    if (!added) {
      alert("Load reports before exporting");
      return;
    }

    XLSX.writeFile(wb, `GST_All_Reports_${fromDate}_to_${toDate}.xlsx`);
  };

  return (
    <div className="container-fluid py-2" style={theme.page}>
      <div style={theme.card}>
        <div style={theme.header}>
          <h6 style={theme.title}>
            <i className="bi bi-file-earmark-spreadsheet me-2"></i>
            GST Final Reports
          </h6>

          <div style={theme.sub}>
            GSTR-1, GSTR-3B, HSN Summary, Tax Liability, Purchase Register and
            Sales Register
          </div>
        </div>

        <div style={theme.body}>
          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label style={theme.label}>From Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={theme.input}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-md-2">
              <label style={theme.label}>To Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={theme.input}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-md-2">
              <label style={theme.label}>Company ID optional</label>
              <input
                type="number"
                className="form-control form-control-sm"
                style={theme.input}
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                placeholder="All/current"
              />
            </div>

            <div className="col-md-6 d-flex gap-2 flex-wrap">
              <button
                className="btn btn-primary btn-sm"
                style={theme.btn}
                onClick={() => loadOne()}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>
                {loading ? "Loading..." : "Load Selected"}
              </button>

              <button
                className="btn btn-dark btn-sm"
                style={theme.btn}
                onClick={loadAll}
                disabled={loading}
              >
                <i className="bi bi-collection me-1"></i>
                Load All
              </button>

              <button
                className="btn btn-success btn-sm"
                style={theme.btn}
                onClick={exportActive}
              >
                <i className="bi bi-file-earmark-excel me-1"></i>
                Export Selected
              </button>

              <button
                className="btn btn-outline-success btn-sm"
                style={theme.btn}
                onClick={exportAll}
              >
                <i className="bi bi-files me-1"></i>
                Export All
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-2 mb-2">
        {[
          ["Taxable", totals.Taxable],
          ["CGST", totals.CGST],
          ["SGST", totals.SGST],
          ["IGST", totals.IGST],
          ["Total Tax", totals.TotalTax],
          ["Grand Total", totals.GrandTotal],
          ["ITC Carry Forward", totals.ITCCarryForward],
        ].map(([k, v]) => (
          <div className="col-md" key={k}>
            <div style={theme.stat}>
              <div className="text-muted" style={{ fontSize: 10 }}>
                {k}
              </div>
              <div className="fw-bold text-primary" style={{ fontSize: 14 }}>
                ₹ {money(v)}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={theme.card}>
        <div className="d-flex flex-wrap gap-1 p-2 border-bottom bg-light">
          {reportTabs.map((tab) => (
            <button
              key={tab.key}
              className={`btn btn-sm ${
                active === tab.key ? "btn-primary" : "btn-outline-primary"
              }`}
              style={theme.btn}
              onClick={() => setActive(tab.key)}
            >
              <i className={`bi ${tab.icon} me-1`}></i>
              {tab.title}
            </button>
          ))}
        </div>

        <div style={theme.body}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-bold">
              <i className={`bi ${currentTab.icon} me-1`}></i>
              {currentTab.title}
            </div>

            <span className="badge text-bg-secondary">{rows.length} records</span>
          </div>

          <DataTable rows={rows} />
        </div>
      </div>
    </div>
  );
}